import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Typography, Select, Row, Col, Input } from "antd";
import { DownloadOutlined, CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { Title } = Typography;

// Reusable check/cross components with AntD icons
const Check = () => <CheckCircleFilled style={{ color: "#52c41a", fontSize: "18px" }} />;
const Cross = () => <CloseCircleFilled style={{ color: "#f5222d", fontSize: "18px" }} />;

export default function CheckListReport() {
    const { authData, backUrl } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [employeeData, setEmployeeData] = useState([]);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        fetchOrganisations();
        fetchEmployeeRecords();
    }, []);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganisations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching organisations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (error) {
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (error) {
            setDesignations([]);
        }
    };

    const fetchEmployeeRecords = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${backUrl}/api/emp/employees/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setEmployeeData(res.data.results || []);
        } catch (error) {
            console.error("Error fetching employee records:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOrgChange = (value) => {
        setSelectedOrg(value);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDepartments(value);
    };

    const handleDeptChange = (value) => {
        setSelectedDept(value);
        setSelectedDesig(null);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDesignations(value);
    };

    const handleDesigChange = (value) => {
        setSelectedDesig(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    /* ---------------- TABLE DATA MAPPING ---------------- */
    const tableData = useMemo(() => {
        return employeeData.map((item, index) => {
            const hasValue = (val) => val !== null && val !== "" && val !== undefined;

            return {
                key: item.id,
                sno: index + 1,
                organisationId: item.organisation,
                departmentId: item.department,
                designationId: item.designation,

                empId: item.emp_code || item.user?.username || "-",
                name: `${item.first_name || ""} ${item.last_name || ""}`.trim() || "-",

                // Checklist fields
                photograph: hasValue(item.photo) ? Check() : Cross(),
                aadhaarCard: hasValue(item.aadhar_card_copy) || hasValue(item.aadhaar_card_number) ? Check() : Cross(),
                panCard: hasValue(item.pan_card_copy) || hasValue(item.pan_card_number) ? Check() : Cross(),
                bankAccount: hasValue(item.bank_account_details) || hasValue(item.bank_account_number) ? Check() : Cross(),
                pf: hasValue(item.uan_number) ? Check() : Cross(),
                esic: hasValue(item.esic_ip_number) ? Check() : Cross(),
                sslc: hasValue(item.SSLC) ? Check() : Cross(),
                hscItiDiploma: hasValue(item.HSC_diploma_ITI) ? Check() : Cross(),
                ugPg: hasValue(item.degree_certificate) || hasValue(item.PG_Phd) ? Check() : Cross(),
                mrfForm: "-", // Not available in sample
                interviewAssessment: "-", // Not available
                resume: "-", // Not directly mapped
                bgvForm: "-", // Background verification
                offerOrder: hasValue(item.offer_letter) ? Check() : Cross(),
                appointmentOrder: hasValue(item.appointment_letter) ? Check() : Cross(),
                salaryAck: "-", // Not available
                applicationForm: "-", // Not available
                offerLetter: hasValue(item.offer_letter) ? Check() : Cross(), // duplicate but kept as per list
                relieveOrder: hasValue(item.releiving_letter) ? Check() : Cross(),
                payslip: hasValue(item.last_three_month_payslip) ? Check() : Cross(),
                accountSummary: hasValue(item.last_three_months_account_statement) ? Check() : Cross(),
                biometricAccess: "-", // Not in API
                accessCard: "-", 
                emailId: hasValue(item.official_email_id) ? Check() : Cross(),
                laptopDesktop: "-", 
                tempIdCard: "-", 
                permanentIdCard: "-", 
                mobileCug: hasValue(item.official_cug_number) ? Check() : Cross(),
                simCard: hasValue(item.official_cug_number) ? Check() : Cross(),
                businessCard: "-", 
                erpId: hasValue(item.user?.username) ? Check() : Cross(), // assuming emp_code is ERP ID
                uniform: "-", 
                shoes: "-", 
                welcomeKit: "-", 
                workStation: "-", 
                bankKit: "-", 
                remark: "", // You can add custom remarks later
            };
        });
    }, [employeeData]);

    /* ---------------- APPLY FILTERS ---------------- */
    const filteredTableData = useMemo(() => {
        let filtered = tableData.filter(row => {
            if (selectedOrg && row.organisationId !== selectedOrg) return false;
            if (selectedDept && row.departmentId !== selectedDept) return false;
            if (selectedDesig && row.designationId !== selectedDesig) return false;

            if (searchText.trim()) {
                const searchLower = searchText.toLowerCase().trim();
                const empIdMatch = row.empId.toLowerCase().includes(searchLower);
                const nameMatch = row.name.toLowerCase().includes(searchLower);
                if (!empIdMatch && !nameMatch) return false;
            }

            return true;
        });

        return filtered.map((row, index) => ({
            ...row,
            sno: index + 1
        }));
    }, [tableData, selectedOrg, selectedDept, selectedDesig, searchText]);

    /* ---------------- TABLE COLUMNS ---------------- */
    const columns = [
        { title: "SNO", dataIndex: "sno", width: 70, fixed: "left" },
        { title: "Emp ID", dataIndex: "empId", width: 110 },
        { title: "Employee Name", dataIndex: "name", width: 180 },
        { title: "Photograph", dataIndex: "photograph", width: 100, align: "center" },
        { title: "Aadhaar card", dataIndex: "aadhaarCard", width: 100, align: "center" },
        { title: "PAN card", dataIndex: "panCard", width: 100, align: "center" },
        { title: "Bank account", dataIndex: "bankAccount", width: 110, align: "center" },
        { title: "PF", dataIndex: "pf", width: 80, align: "center" },
        { title: "ESIC", dataIndex: "esic", width: 80, align: "center" },
        { title: "10TH", dataIndex: "sslc", width: 80, align: "center" },
        { title: "12TH/ITI/DIPLOMA", dataIndex: "hscItiDiploma", width: 130, align: "center" },
        { title: "UG / PG", dataIndex: "ugPg", width: 100, align: "center" },
        { title: "MRF Form", dataIndex: "mrfForm", width: 100, align: "center" },
        { title: "Interview Assessment Sheet", dataIndex: "interviewAssessment", width: 160, align: "center" },
        { title: "Resume", dataIndex: "resume", width: 90, align: "center" },
        { title: "Background Verification Form", dataIndex: "bgvForm", width: 160, align: "center" },
        { title: "Offer order", dataIndex: "offerOrder", width: 110, align: "center" },
        { title: "Appointment Order", dataIndex: "appointmentOrder", width: 140, align: "center" },
        { title: "Salary Acknowledgement", dataIndex: "salaryAck", width: 140, align: "center" },
        { title: "Application form", dataIndex: "applicationForm", width: 120, align: "center" },
        { title: "Offer letter", dataIndex: "offerLetter", width: 110, align: "center" },
        { title: "Relieve Order", dataIndex: "relieveOrder", width: 110, align: "center" },
        { title: "Payslip", dataIndex: "payslip", width: 90, align: "center" },
        { title: "Account Summary", dataIndex: "accountSummary", width: 130, align: "center" },
        { title: "Biometric Access", dataIndex: "biometricAccess", width: 120, align: "center" },
        { title: "Access Card", dataIndex: "accessCard", width: 110, align: "center" },
        { title: "E-mail ID", dataIndex: "emailId", width: 100, align: "center" },
        { title: "Laptop/Desktop", dataIndex: "laptopDesktop", width: 120, align: "center" },
        { title: "Temporary Id Card", dataIndex: "tempIdCard", width: 130, align: "center" },
        { title: "Permanent ID card", dataIndex: "permanentIdCard", width: 130, align: "center" },
        { title: "Mobile CUG", dataIndex: "mobileCug", width: 110, align: "center" },
        { title: "Sim Card", dataIndex: "simCard", width: 100, align: "center" },
        { title: "Business Card", dataIndex: "businessCard", width: 110, align: "center" },
        { title: "ERP ID", dataIndex: "erpId", width: 90, align: "center" },
        { title: "Uniform", dataIndex: "uniform", width: 90, align: "center" },
        { title: "Shoes", dataIndex: "shoes", width: 80, align: "center" },
        { title: "Welcome Kit's", dataIndex: "welcomeKit", width: 110, align: "center" },
        { title: "Work Station / cabin", dataIndex: "workStation", width: 140, align: "center" },
        { title: "Bank Kit's", dataIndex: "bankKit", width: 100, align: "center" },
        { title: "Remark", dataIndex: "remark", width: 200 },
    ];

    /* ---------------- EXCEL DOWNLOAD ---------------- */
    const downloadExcel = () => {
        const excelData = filteredTableData.map(row => ({
            "SNO": row.sno,
            "Emp ID": row.empId,
            "Employee Name": row.name,
            "Photograph": row.photograph,
            "Aadhaar card": row.aadhaarCard,
            "PAN card": row.panCard,
            "Bank account": row.bankAccount,
            "PF": row.pf,
            "ESIC": row.esic,
            "10TH": row.sslc,
            "12TH/ITI/DIPLOMA": row.hscItiDiploma,
            "UG / PG": row.ugPg,
            "MRF Form": row.mrfForm,
            "Interview Assessment Sheet": row.interviewAssessment,
            "Resume": row.resume,
            "Background Verification Form": row.bgvForm,
            "Offer order": row.offerOrder,
            "Appointment Order": row.appointmentOrder,
            "Salary Acknowledgement": row.salaryAck,
            "Application form": row.applicationForm,
            "Offer letter": row.offerLetter,
            "Relieve Order": row.relieveOrder,
            "Payslip": row.payslip,
            "Account Summary": row.accountSummary,
            "Biometric Access": row.biometricAccess,
            "Access Card": row.accessCard,
            "E-mail ID": row.emailId,
            "Laptop/Desktop": row.laptopDesktop,
            "Temporary Id Card": row.tempIdCard,
            "Permanent ID card": row.permanentIdCard,
            "Mobile CUG": row.mobileCug,
            "Sim Card": row.simCard,
            "Business Card": row.businessCard,
            "ERP ID": row.erpId,
            "Uniform": row.uniform,
            "Shoes": row.shoes,
            "Welcome Kit's": row.welcomeKit,
            "Work Station / cabin": row.workStation,
            "Bank Kit's": row.bankKit,
            "Remark": row.remark,
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Checklist Report");

        // Style header
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_cell({ r: 0, c: C });
            if (!worksheet[address]) continue;
            worksheet[address].s = {
                fill: { fgColor: { rgb: "0e497b" } },
                font: { color: { rgb: "FFFFFF" }, bold: true },
                alignment: { horizontal: "center", wrapText: true }
            };
        }

        // Auto-size columns (basic)
        worksheet["!cols"] = Array(excelData[0] ? Object.keys(excelData[0]).length : 10).fill({ wch: 12 });

        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer]), "Employee_Onboarding_Checklist_Report.xlsx");
    };

    return (
        <>
            <Title level={4}>Checklist Report</Title>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={5}>
                    <Select
                        placeholder="Select Organisation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        value={selectedOrg}
                        onChange={handleOrgChange}
                        style={{ width: "100%" }}
                    >
                        {organisations.map(org => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Department"
                        allowClear
                        showSearch
                        disabled={!selectedOrg}
                        value={selectedDept}
                        onChange={handleDeptChange}
                        style={{ width: "100%" }}
                    >
                        {departments.map(dept => (
                            <Select.Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Designation"
                        allowClear
                        showSearch
                        disabled={!selectedDept}
                        value={selectedDesig}
                        onChange={handleDesigChange}
                        style={{ width: "100%" }}
                    >
                        {designations.map(des => (
                            <Select.Option key={des.id} value={des.id}>
                                {des.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Input
                        placeholder="Search by Employee ID or Name"
                        allowClear
                        value={searchText}
                        onChange={handleSearchChange}
                        style={{ width: "100%" }}
                    />
                </Col>

                <Col span={4} style={{ textAlign: "right" }}>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadExcel}
                        style={{ backgroundColor: "#0e497b" }}
                    >
                        Download Excel
                    </Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={filteredTableData}
                loading={loading}
                bordered
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: filteredTableData.length,
                    onChange: page => setCurrentPage(page),
                    showSizeChanger: false
                }}
                scroll={{ x: 4000 }} // Wide table
                components={{
                    header: {
                        cell: props => (
                            <th {...props} style={{ background: "#0e497b", color: "#fff", textAlign: "center" }}>
                                {props.children}
                            </th>
                        )
                    }
                }}
            />
        </>
    );
}